import useBrand from "@RHCommerceDev/hooks/useBrand";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import {
  countryAbbreviationMapper as AbbreviationMapper,
  countries
} from "@RHCommerceDev/resources/countries-config.json";
import { cn } from "@RHCommerceDev/utils/cn";
import { usePageContent } from "customProviders/LocationProvider";
import { useCookiesWithPermission } from "hooks/useCookiesWithPermission";
import React, { useMemo } from "react";
import { Typography } from "unstyled-ui-components";
import {
  BRAND_NAME_TO_BRAND_CODE,
  NA_COUNTRIES,
  TRAILING_PARENTHESIS_REGEX
} from "@RHCommerceDev/utils/constants";
import memoize from "@RHCommerceDev/utils/memoize";
import yn from "yn";
import { useHeaderAtom } from "hooks/atoms/useHeaderAtom";

const HamburgerCountrySelector = () => {
  const brand = useBrand();
  const env = useEnv();
  const {
    country: selectedCountry,
    language: selectedLanguage,
    setCountry,
    setLanguage,
    setMeasurement,
    handleSaveCookies
  } = useUserPreferences();
  const { setCookieWrapper } = useCookiesWithPermission();

  const { resetAll } = useHeaderAtom();

  const FEATURE_EU_EXPANSION_FR = yn(env?.FEATURE_EU_EXPANSION_FR);
  const FEATURE_MONTREAL = yn(env?.FEATURE_MONTREAL);
  const FEATURE_EU_MONACO = yn(env?.FEATURE_EU_MONACO);

  const { pageContent: pageContentObj } = usePageContent();
  const countrySelectorContent =
    ((pageContentObj?.globalHeader as Record<string, unknown>)
      ?.countries as Record<string, unknown>) ?? {};

  const monacoAsTheShippingCountry = !processEnvServer
    ? !!localStorage.getItem("shippingCountry")
    : false;

  const {
    countries: availableCountries = Object.fromEntries(
      Object.entries(countries).map(([country, data]) => [
        country,
        data.labelEN
      ])
    ),
    countryAbbreviationMapper = AbbreviationMapper
  } = countrySelectorContent;

  const countryNameMapper: Record<string, string> = Object.fromEntries(
    Object.entries(availableCountries as []).map(([key, value]) => [
      key,
      (value as string)?.replace(TRAILING_PARENTHESIS_REGEX, "")
    ])
  );

  const filteredCountryList = useMemo(() => {
    const countryList = Object.keys(availableCountries as []);

    if (
      brand === BRAND_NAME_TO_BRAND_CODE["BABY & CHILD"] ||
      brand === BRAND_NAME_TO_BRAND_CODE["TEEN"]
    ) {
      return countryList.filter(country =>
        NA_COUNTRIES.includes(country as "US" | "CA")
      );
    }

    if (!FEATURE_EU_EXPANSION_FR) {
      const index = countryList.indexOf("FR");
      if (index !== -1) countryList.splice(index, 1);
    }

    countryList.sort((a, b) => {
      if (a === "US") return -1;
      if (b === "US") return 1;
      return countryNameMapper[a].localeCompare(countryNameMapper[b]);
    });

    return countryList;
  }, [availableCountries, countryNameMapper, FEATURE_EU_EXPANSION_FR, brand]);

  const countryDisplayList = useMemo(
    () => [
      selectedCountry,
      ...filteredCountryList.filter(country => country !== selectedCountry)
    ],
    [selectedCountry, filteredCountryList]
  );

  const handleCountryChange = (country, changedLanguage?: string) => {
    const {
      country: newCountry,
      measurement: newMeasurement,
      language: defaultLanguage
    } = countries[country as keyof typeof countries].defaultValues;

    const newLanguage = changedLanguage || defaultLanguage;

    document?.body?.dispatchEvent(
      new CustomEvent("cta_click", {
        detail: {
          item: {
            fromHeader: true,
            fromHamburger: true,
            country: newCountry,
            language: newLanguage,
            measurement: newMeasurement
          }
        }
      })
    );

    setCountry(newCountry);
    setLanguage(newLanguage);
    setMeasurement(newMeasurement);
    // if (state.isHamburgerDropdownOpen) {
    //   closeHamburgerDropdown();
    // }
    handleSaveCookies(
      {
        country: newCountry,
        language: newLanguage,
        measurement: newMeasurement
      },
      undefined,
      undefined,
      true
    );

    setCookieWrapper("manual_country_selection", true);

    if (FEATURE_EU_MONACO && monacoAsTheShippingCountry) {
      localStorage.removeItem("shippingCountry");
    }

    //reset all state values to make sure no state is active on country change
    resetAll();
  };

  return (
    <div className="flex flex-col gap-4 px-0">
      {countryDisplayList?.map((country, index) => (
        <button
          key={index}
          onClick={() => {
            if (country !== selectedCountry) {
              handleCountryChange(country);
            }
          }}
          className={cn(
            "grid grid-cols-[1fr_2fr] items-baseline gap-x-5 p-0 text-white"
          )}
        >
          <Typography
            variant="caption"
            fontFamily="rhc"
            fontWeight="thin"
            className={cn(
              "whitespace-nowrap pl-0.5 text-base capitalize leading-[1.5em] tracking-[0.05em] text-inherit",
              { "font-semibold": country === selectedCountry }
            )}
          >
            {
              (countryAbbreviationMapper as Record<string, string>)[
                country as string
              ]
            }
          </Typography>

          {((FEATURE_MONTREAL && country === "CA") ||
            (FEATURE_EU_EXPANSION_FR && country === "FR")) && (
            <div className="cursor flex gap-2.5">
              {countries[country].availableLanguages.map(
                (language, languageIndex) => (
                  <div
                    key={languageIndex}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleCountryChange(country, language);
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontFamily="rhc"
                      fontWeight="thin"
                      className={cn(
                        "whitespace-nowrap text-base capitalize leading-[1.5em] tracking-[0.05em]",
                        {
                          "font-semibold":
                            country === selectedCountry &&
                            language === selectedLanguage
                        }
                      )}
                    >
                      {language.split("-")[0].toUpperCase()}
                    </Typography>
                  </div>
                )
              )}
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

export default memoize(HamburgerCountrySelector);
